import { useEffect, useState } from 'react';
import standardEventTypes from '../constants/eventTypes';
import useFetch from './useFetch';

// Returns `undefined` while loading, `null` if the load failed, and an array of
// `{ label, value }` event types once loaded. Consumers must handle all three —
// check for the error state before treating a falsy value as "still loading",
// otherwise a failed load renders a spinner forever.
//
// Two independent restrictions, strongest first:
//
// `allowedEventsForTrigger` — the campaign-trigger allowlist the API applies: the
// events this domain has *already observed*, minus the high-frequency ones, plus
// any a super admin allowed for it explicitly.
//
// `excludeHighFrequency` — drops only the events flagged `highFrequency` in the
// standard list. Every other standard event stays on offer whether or not the
// domain has emitted it yet. Use this where volume is the only concern and there
// is no reason to wait for an event to fire before it can be configured.
const useEventTypes = (accountId, domainId, setLoadError, options = {}) => {
  const fetch = useFetch();
  const [eventTypes, setEventTypes] = useState();

  // Read off `options` here so the effect below depends on primitives — callers pass
  // an object literal, which is a new object on every render and would refetch forever.
  const { allowedEventsForTrigger = false, excludeHighFrequency = false } = options;

  useEffect(() => {
    const defaultEventTypeIds = new Set(standardEventTypes.map((e) => e.value));
    const customEventTypes = [];

    setEventTypes(undefined);

    const url = `/api/admin/accounts/${accountId}/domains/${domainId}/eventActions${
      allowedEventsForTrigger ? '?allowedEventsForTrigger=true' : ''
    }`;

    fetch(url)
      .then(({ eventActions }) => {
        const allowedEventActions = new Set(eventActions);

        eventActions.forEach((action) => {
          if (defaultEventTypeIds.has(action)) {
            return;
          }

          if (action.startsWith('Catalog trigger - ')) {
            customEventTypes.push({ label: action, value: action });
            return;
          }

          customEventTypes.push({ label: `Custom Event: ${action}`, value: action });
        });

        // Filter standard event types based on what's allowed
        let filteredStandardEventTypes = standardEventTypes;
        if (allowedEventsForTrigger) {
          filteredStandardEventTypes = standardEventTypes.filter((eventType) =>
            allowedEventActions.has(eventType.value)
          );
        } else if (excludeHighFrequency) {
          filteredStandardEventTypes = standardEventTypes.filter((eventType) => !eventType.highFrequency);
        }

        setEventTypes([...filteredStandardEventTypes, ...customEventTypes]);
      })
      .catch((error) => {
        setEventTypes(null);
        setLoadError(error);
      });
  }, [fetch, accountId, domainId, setLoadError, allowedEventsForTrigger, excludeHighFrequency]);

  return eventTypes;
};

export default useEventTypes;
